// components/OutfitBuilder.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  Search, 
  Filter,
  ExternalLink,
  GripVertical,
  Trash2,
  Save
} from 'lucide-react';
import { dbService } from '../lib/supabase';

const OutfitBuilder = () => {
  const { id: capsuleId, outfitId } = useParams();
  const navigate = useNavigate();
  
  const [outfit, setOutfit] = useState(null);
  const [capsule, setCapsule] = useState(null);
  const [outfitItems, setOutfitItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [showAddItems, setShowAddItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [outfitName, setOutfitName] = useState('');
  const [outfitDescription, setOutfitDescription] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);

  useEffect(() => {
    loadOutfit();
    loadCapsule();
  }, [outfitId, capsuleId]);

  useEffect(() => {
    if (capsule) {
      loadCapsuleItems();
    }
  }, [capsule]);

  const loadOutfit = async () => {
    setLoading(true);
    try {
      const { data: outfitData, error } = await dbService.getOutfit(outfitId);
      if (!error && outfitData) {
        setOutfit(outfitData);
        setOutfitName(outfitData.name);
        setOutfitDescription(outfitData.description || '');
        setOutfitItems(outfitData.items || []);
      }
    } catch (error) {
      console.error('Error loading outfit:', error);
    }
    setLoading(false);
  };

  const loadCapsule = async () => {
    try {
      const { data: capsuleData, error } = await dbService.getCapsule(capsuleId);
      if (!error && capsuleData) {
        setCapsule(capsuleData);
      }
    } catch (error) {
      console.error('Error loading capsule:', error);
    }
  };

  const loadCapsuleItems = async () => {
    const { data, error } = await dbService.getItemsForCapsule(capsuleId);
    if (!error) {
      setAllItems(data || []);
      const uniqueCategories = [...new Set(data?.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories);
    }
  };

  const handleUpdateOutfitInfo = async () => {
    setSaving(true);
    const { error } = await dbService.updateOutfit(outfitId, {
      name: outfitName,
      description: outfitDescription
    });
    
    if (!error) {
      setOutfit(prev => ({
        ...prev,
        name: outfitName,
        description: outfitDescription
      }));
      setIsEditingName(false);
    }
    setSaving(false);
  };

  const handleAddItemToOutfit = async (item) => {
    // Check if item is already in outfit
    if (outfitItems.some(outfitItem => outfitItem.id === item.id)) {
      alert('Item is already in this outfit');
      return;
    }

    const { error } = await dbService.addItemToOutfit(outfitId, item.id);
    if (!error) {
      setOutfitItems(prev => [...prev, item]);
    }
  };

  const handleRemoveItemFromOutfit = async (itemId) => {
    const { error } = await dbService.removeItemFromOutfit(outfitId, itemId);
    if (!error) {
      setOutfitItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const handleReorderItems = async (dragIndex, hoverIndex) => {
    const newItems = [...outfitItems];
    const draggedItem = newItems[dragIndex];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);
    
    setOutfitItems(newItems);
    
    // Update order in database
    const updates = newItems.map((item, index) => ({
      item_id: item.id,
      order_index: index
    }));
    
    await dbService.updateOutfitItemOrder(outfitId, updates);
  };

  // Filter available items (exclude items already in outfit)
  const availableItems = allItems.filter(item => 
    !outfitItems.some(outfitItem => outfitItem.id === item.id)
  );

  const filteredAvailableItems = availableItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || item.category === filterBy;
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!outfit) {
    return <div className="text-center py-8">Outfit not found</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button
          onClick={() => navigate(`/dashboard/capsules`)}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          {isEditingName ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={outfitName}
                onChange={(e) => setOutfitName(e.target.value)}
                className="text-2xl font-light bg-transparent border-b border-gray-300 focus:border-gray-500 outline-none"
                onBlur={handleUpdateOutfitInfo}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdateOutfitInfo()}
                autoFocus
              />
              <button
                onClick={handleUpdateOutfitInfo}
                disabled={saving}
                className="text-green-600 hover:text-green-800"
              >
                <Save className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <h1 
              className="text-3xl font-light text-gray-900 cursor-pointer hover:text-gray-700"
              onClick={() => setIsEditingName(true)}
            >
              {outfitName}
            </h1>
          )}
          <div className="text-gray-600 mt-1">
            {capsule?.clients?.name && <span>{capsule.clients.name} • </span>}
            {capsule?.name}
          </div>
        </div>
        <button
          onClick={() => setShowAddItems(true)}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" />
          <span>Add Items</span>
        </button>
      </div>

      {/* Current Outfit Items */}
      <div className="mb-8">
        <h2 className="text-xl font-medium mb-4">
          Current Outfit ({outfitItems.length} items)
        </h2>
        
        {outfitItems.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500 mb-4">No items in this outfit yet</p>
            <button
              onClick={() => setShowAddItems(true)}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800"
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {outfitItems.map((item, index) => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden relative group"
              >
                {/* Drag handle */}
                <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </div>
                
                {/* Remove button */}
                <button
                  onClick={() => handleRemoveItemFromOutfit(item.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>

                {item.photo_url && (
                  <img
                    src={item.photo_url}
                    alt={item.name}
                    className="w-full h-48 object-cover"
                  />
                )}
                
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
                  <div className="space-y-1 text-sm text-gray-600 mt-2">
                    {item.brand && <p className="font-medium">{item.brand}</p>}
                    {item.category && (
                      <p className="text-xs bg-gray-100 px-2 py-1 rounded-full inline-block">
                        {item.category}
                      </p>
                    )}
                    {item.price && <p className="font-medium text-gray-900">{item.price}</p>}
                  </div>
                  
                  {item.link && (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
                    >
                      <span>Shop Now</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Items Modal */}
      {showAddItems && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-xl font-medium">Add Items to Outfit</h3>
              <button
                onClick={() => setShowAddItems(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b bg-gray-50">
              <p className="text-sm text-gray-600">
                Selecting from <strong>{capsule?.name}</strong> items • {availableItems.length} items available
              </p>
            </div>
            
            {/* Search and Filter */}
            <div className="p-6 border-b">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="text-gray-400 w-4 h-4" />
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Available Items */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredAvailableItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleAddItemToOutfit(item)}
                  >
                    {item.photo_url && (
                      <img
                        src={item.photo_url}
                        alt={item.name}
                        className="w-full h-32 object-cover"
                      />
                    )}
                    <div className="p-3">
                      <h4 className="font-medium text-gray-900 text-sm truncate">{item.name}</h4>
                      <div className="text-xs text-gray-600 mt-1">
                        {item.brand && <p>{item.brand}</p>}
                        {item.category && (
                          <span className="bg-gray-200 px-2 py-1 rounded-full inline-block mt-1">
                            {item.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredAvailableItems.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {searchTerm || filterBy !== 'all' 
                      ? 'No items match your search.' 
                      : 'All items are already in this outfit.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutfitBuilder;